import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import './Toast.styl';

import { ToastContext, type ToastTone } from './context';
import { Icon, type IconName } from '../../atoms/Icon';

type ToastItem = { id: number; message: string; tone: ToastTone; leaving?: boolean };

/** How long a notification stays (the bar at its foot shows the time left). */
const DURATION_MS = 4500;
/** The exit animation, before the item leaves the DOM. */
const LEAVE_MS = 280;

const ICON: Record<ToastTone, IconName> = { success: 'check', error: 'close', info: 'bell', warning: 'warning' };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<number, number>());

  // fades out first, then leaves the list
  const dismiss = useCallback((id: number) => {
    window.clearTimeout(timers.current.get(id));
    setItems((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    timers.current.set(
      id,
      window.setTimeout(() => {
        timers.current.delete(id);
        setItems((prev) => prev.filter((t) => t.id !== id));
      }, LEAVE_MS)
    );
  }, []);

  const show = useCallback(
    (message: string, tone: ToastTone = 'info') => {
      const id = Date.now() + Math.random();
      setItems((prev) => [...prev.slice(-3), { id, message, tone }]);
      timers.current.set(id, window.setTimeout(() => dismiss(id), DURATION_MS));
    },
    [dismiss]
  );

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="o-toast" aria-live="polite">
        {items.map((t) => (
          <Toast key={t.id} item={t} onClose={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function Toast({ item, onClose }: { item: ToastItem; onClose: () => void }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'toast' });
  return (
    <div className={`o-toast__item o-toast__item--${item.tone}${item.leaving ? ' o-toast__item--leaving' : ''}`} role={item.tone === 'error' ? 'alert' : 'status'}>
      {/* the app's abstract shapes, in white, behind the content */}
      <span className="o-toast__shape o-toast__shape--one" aria-hidden="true" />
      <span className="o-toast__shape o-toast__shape--two" aria-hidden="true" />
      <span className="o-toast__shape o-toast__shape--rings" aria-hidden="true" />
      <span className="o-toast__shape o-toast__shape--arc" aria-hidden="true" />
      <span className="o-toast__icon" aria-hidden="true">
        <Icon name={ICON[item.tone]} size={17} strokeWidth={2.4} />
      </span>
      <div className="o-toast__content">
        <div className="o-toast__title">{t(`title.${item.tone}`)}</div>
        <div className="o-toast__message">{item.message}</div>
      </div>
      <button type="button" className="o-toast__close" onClick={onClose} aria-label={t('close')}>
        <Icon name="close" size={14} strokeWidth={2} />
      </button>
      <span className="o-toast__progress" aria-hidden="true" />
    </div>
  );
}
